import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Signature on-load moment: a 3D-style capsule sits center-stage, then cracks
 * open — the two halves fly apart, a burst of pills/particles scatters, a light
 * blooms and wipes away to reveal the hero underneath.
 */
export default function Intro({ onDone }) {
  const [phase, setPhase] = useState("closed"); // closed -> open -> exit
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("open"), 650);
    const t2 = setTimeout(() => setPhase("exit"), 1650);
    const t3 = setTimeout(() => {
      setShow(false);
      onDone?.();
    }, 2450);
    // reveal hero text slightly before overlay fully leaves
    const t4 = setTimeout(() => onDone?.(), 1700);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [onDone]);

  const particles = Array.from({ length: 14 });

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center overflow-hidden bg-white"
          initial={{ opacity: 1 }}
          animate={phase === "exit" ? { clipPath: "circle(0% at 50% 42%)" } : {}}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        >
          <div className="grid-pattern absolute inset-0 opacity-40" />

          {/* light bloom */}
          <motion.div
            className="absolute h-40 w-40 rounded-full bg-lab-green/40 blur-3xl"
            initial={{ scale: 0, opacity: 0 }}
            animate={phase !== "closed" ? { scale: 6, opacity: [0, 0.7, 0] } : {}}
            transition={{ duration: 1.4, ease: "easeOut" }}
          />

          {/* particles burst */}
          {particles.map((_, i) => {
            const angle = (i / particles.length) * Math.PI * 2;
            const dist = 160 + (i % 3) * 60;
            return (
              <motion.span
                key={i}
                className={`absolute rounded-full ${i % 2 ? "bg-blue-brand" : "bg-lab-green"}`}
                style={{ width: 10 + (i % 3) * 4, height: 10 + (i % 3) * 4 }}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                animate={
                  phase !== "closed"
                    ? {
                        x: Math.cos(angle) * dist,
                        y: Math.sin(angle) * dist,
                        opacity: [0, 1, 0],
                        scale: [0, 1, 0.4],
                      }
                    : {}
                }
                transition={{ duration: 1.2, ease: "easeOut", delay: 0.05 }}
              />
            );
          })}

          {/* the capsule */}
          <motion.div
            className="relative"
            initial={{ scale: 0, rotate: -35, opacity: 0 }}
            animate={{ scale: 1, rotate: -20, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <div className="relative flex h-28 w-64" style={{ perspective: 800 }}>
              {/* left (blue) half */}
              <motion.div
                className="h-full w-1/2 rounded-l-full bg-blue-brand shadow-[inset_-8px_0_20px_rgba(0,0,0,0.15)]"
                initial={{ x: 0, rotateZ: 0, opacity: 1 }}
                animate={phase !== "closed" ? { x: -220, rotateZ: -25, opacity: 0 } : {}}
                transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              >
                <div className="ml-6 mt-6 h-4 w-16 rounded-full bg-white/40" />
              </motion.div>
              {/* right (green) half */}
              <motion.div
                className="h-full w-1/2 rounded-r-full bg-lab-green shadow-[inset_8px_0_20px_rgba(0,0,0,0.12)]"
                initial={{ x: 0, rotateZ: 0, opacity: 1 }}
                animate={phase !== "closed" ? { x: 220, rotateZ: 25, opacity: 0 } : {}}
                transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              />
            </div>
          </motion.div>

          <motion.p
            className="absolute bottom-16 font-mono text-[11px] uppercase tracking-[0.4em] text-slate-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "closed" ? 1 : 0 }}
            transition={{ duration: 0.4 }}
          >
            Content Lab
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
